<template>
  <div class="help-center">
    <div class="help-header">
      <div class="header-main">
        <div class="header-left">
          <h1 class="help-title">Help Center</h1>
        </div>
        <div class="header-right">
          <div class="email-section">
            <span class="email-label">Contact:</span>
            <span class="email-address">pam@toplinemarketing.net</span>
            <button @click="copyEmail" class="copy-button" :class="{ 'copied': emailCopied }">
              <v-icon>{{ emailCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              {{ emailCopied ? 'Copied!' : 'Copy' }}
            </button>
          </div>
        </div>
      </div>
      <div class="search-container">
        <input type="text" placeholder="Search..." v-model="searchQuery" class="search-input" />
      </div>
    </div>
    <div class="faq-container">
      <div v-if="loading">
        <!-- Skeleton Loaders -->
        <div v-for="categoryIndex in 3" :key="categoryIndex" class="skeleton-category-section">
          <!-- Skeleton Category Title -->
          <div class="skeleton-category-title">
            <div class="skeleton-box skeleton-category-text"></div>
          </div>
          
          <!-- Skeleton FAQ Items -->
          <div v-for="i in 3" :key="i" class="skeleton-faq-item">
            <div class="skeleton-faq-question">
              <div class="skeleton-box skeleton-question-text" :style="{ width: (60 + (i * 10)) + '%' }"></div>
              <div class="skeleton-box skeleton-icon"></div>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button @click="fetchFaqCategories" class="retry-button">Try Again</button>
      </div>
      
      <template v-else>
        <template v-if="!searchQuery">
          <div v-for="(categoryFaqs, category) in groupedFaqs" :key="category" class="category-section">
            <h2 class="category-title">{{ category }}</h2>
            <div 
              v-for="(faq, index) in categoryFaqs" 
              :key="index" 
              class="faq-item"
              :class="{ 'active': faq.isOpen }"
              @click="toggleFaq(getFaqIndex(faq))"
            >
              <div class="faq-question">
                <div>{{ faq.question }}</div>
                <v-icon>{{ faq.isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
              <div v-if="faq.isOpen" class="faq-answer">
                {{ faq.answer }}
              </div>
            </div>
          </div>
        </template>
        
        <template v-else>
          <h2 v-if="filteredFaqs.length > 0" class="search-results">
            Search Results ({{ filteredFaqs.length }})
          </h2>
          <h2 v-else class="no-results">No results found for "{{ searchQuery }}"</h2>
          
          <div 
            v-for="(faq, index) in filteredFaqs" 
            :key="index" 
            class="faq-item"
            :class="{ 'active': faq.isOpen }"
            @click="toggleFaq(getFaqIndex(faq))"
          >
            <div class="faq-category-tag">{{ faq.categoryName || 'General' }}</div>
            <div class="faq-question">
              <div>{{ faq.question }}</div>
              <v-icon>{{ faq.isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
            <div v-if="faq.isOpen" class="faq-answer">
              {{ faq.answer }}
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';

export default {
  name: 'HelpCenter',
  components: {},
  data() {
    return {
      emailCopied: false,
      searchQuery: '',
      faqs: [],
      loading: false,
      error: null
    };
  },
  computed: {
    filteredFaqs() {
      if (!this.searchQuery) {
        return this.faqs;
      }
      const query = this.searchQuery.toLowerCase();
      return this.faqs.filter(faq => 
        faq.question.toLowerCase().includes(query) || 
        faq.answer.toLowerCase().includes(query)
      );
    },
    groupedFaqs() {
      const grouped = {};
      this.faqs.forEach(faq => {
        const category = faq.categoryName || 'General';
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(faq);
      });
      return grouped;
    }
  },
  methods: {
    copyEmail() {
      const email = 'pam@toplinemarketing.net';

      // Prefer the modern Clipboard API if available and permitted
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(email)
          .then(() => {
            this.setCopied();
          })
          .catch(err => {
            console.error('Error using Clipboard API:', err);
            // Use fallback if Clipboard API fails (e.g., insecure context)
            this.fallbackCopy(email);
          });
      } else {
        // Fallback for browsers without Clipboard API support
        this.fallbackCopy(email);
      }
    },

    // Fallback copy routine using a temporary textarea element
    fallbackCopy(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      // Position textarea off-screen and make it invisible
      textArea.style.position = 'fixed';
      textArea.style.top = '-9999px';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          this.setCopied();
        } else {
          console.error('document.execCommand("copy") returned false.');
        }
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textArea);
    },

    // Helper to set and automatically reset the copied state
    setCopied() {
      this.emailCopied = true;
      setTimeout(() => {
        this.emailCopied = false;
      }, 2000);
    },
    toggleFaq(index) {
      const updatedFaqs = [...this.faqs];
      updatedFaqs[index].isOpen = !updatedFaqs[index].isOpen;
      this.faqs = updatedFaqs;
    },
    getFaqIndex(faq) {
      return this.faqs.findIndex(f => f.id === faq.id);
    },
    fetchFaqs() {
      this.loading = true;
      this.error = null;
      apiService.get('/business/faqs/')
        .then(response => {
          const faqResults = response.data.results || [];
          this.faqs = faqResults.map(faq => ({
            ...faq,
            isOpen: false
          }));
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching FAQs:', error);
          this.error = 'Failed to load FAQs. Please try again later.';
          this.loading = false;
          this.faqs = this.getPlaceholderFaqs();
        });
    },
    fetchFaqCategories() {
      apiService.get('/business/faq-categories/')
        .then(response => {
          const categoryResults = response.data.results || [];
          if (categoryResults.length > 0) {
            this.fetchFaqsWithCategories(categoryResults);
          } else {
            this.fetchFaqs();
          }
        })
        .catch(error => {
          console.error('Error fetching FAQ categories:', error);
          this.fetchFaqs();
        });
    },
    fetchFaqsWithCategories(categories) {
      this.loading = true;
      apiService.get('/business/faqs/')
        .then(response => {
          const faqResults = response.data.results || [];
          const categoryMap = {};
          categories.forEach(category => {
            categoryMap[category.id] = category.name;
          });
          this.faqs = faqResults.map(faq => ({
            ...faq,
            categoryName: categoryMap[faq.category] || 'General',
            isOpen: false
          }));
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching FAQs with categories:', error);
          this.error = 'Failed to load FAQs. Please try again later.';
          this.loading = false;
          this.faqs = this.getPlaceholderFaqs();
        });
    },
    getPlaceholderFaqs() {
      return [
        { 
          id: 1,
          question: 'How do I reset my password?', 
          answer: 'You can reset your password by clicking on the "Forgot Password" link on the login page.', 
          isOpen: false,
          categoryName: 'Account Management'
        },
        { 
          id: 2,
          question: 'How do I update my profile?', 
          answer: 'Go to your Account page by clicking on your profile picture in the sidebar, then select "Account" to edit your details.', 
          isOpen: false,
          categoryName: 'Account Management' 
        },
        { 
          id: 3,
          question: 'When do I get paid?', 
          answer: 'Payments are processed on the 15th and 30th of each month for all approved payouts.', 
          isOpen: false,
          categoryName: 'Payments'
        },
        { 
          id: 4,
          question: 'How do I contact support?', 
          answer: 'You can reach our support team by emailing support@topline.com or using the contact form in your dashboard.', 
          isOpen: false,
          categoryName: 'Support'
        },
        { 
          id: 5,
          question: 'What file formats are supported for uploads?', 
          answer: 'We support PNG, JPG, PDF, and DOCX file formats for uploads.', 
          isOpen: false,
          categoryName: 'Usage'
        },
        { 
          id: 6,
          question: 'How can I become an admin?', 
          answer: 'Admin roles are assigned by the Super Admin. Please contact your organization lead for more information.', 
          isOpen: false,
          categoryName: 'Roles & Permissions'
        },
        { 
          id: 7,
          question: 'Is my data secure?', 
          answer: 'Yes, we use industry-standard encryption and security protocols to keep your data safe and secure.', 
          isOpen: false,
          categoryName: 'Security'
        },
        { 
          id: 8,
          question: 'Can I use this platform on mobile devices?', 
          answer: 'Yes, our platform is fully responsive and works on desktop, tablet, and mobile devices.', 
          isOpen: false,
          categoryName: 'Usage'
        }
      ];
    }
  },
  created() {
    this.fetchFaqCategories();
  }
};
</script>

<style scoped>
.help-center {
  min-height: 100vh;
  background-color: #f9f9f9;
  display: flex;
  flex-direction: column;
}

.help-header {
  background-color: #20232a;
  color: white;
  padding: 15px 20px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  margin: 20px 20px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  width: 100%;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-right {
  display: flex;
  align-items: center;
}

.help-title {
  font-size: 2.2rem;
  font-weight: 300;
  margin: 0;
  color: #fff;
}

.email-section {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
}

.email-label {
  font-weight: 500;
  color: #e0e0e0;
}

.email-address {
  background-color: #fff;
  color: #20232a;
  padding: 8px 15px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  border: none;
}

.copy-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 15px;
  background-color: #e94057;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  min-width: 80px;
}

.copy-button:hover {
  background-color: #d13048;
  transform: translateY(-1px);
}

.copy-button.copied {
  background-color: #4caf50;
}

.copy-button.copied:hover {
  background-color: #45a049;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .help-header {
    margin: 15px 15px 0;
  }
  
  .header-main {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .header-right {
    width: 100%;
    justify-content: flex-start;
  }
  
  .email-section {
    gap: 8px;
    flex-wrap: wrap;
  }
  
  .email-address {
    font-size: 0.9rem;
    padding: 8px 12px;
    word-break: break-all;
  }
  
  .copy-button {
    padding: 8px 12px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .help-header {
    margin: 10px 10px 0;
    padding: 12px 15px;
  }
  
  .help-title {
    font-size: 1.8rem;
  }
  
  .email-section {
    gap: 5px;
  }
  
  .email-address {
    font-size: 0.8rem;
    padding: 6px 10px;
  }
  
  .email-label {
    display: none;
  }
}

.search-container {
  width: 100%;
}

.search-input {
  width: 100%;
  padding: 15px 20px;
  font-size: 16px;
  border: none;
  border-radius: 4px;
  background-color: white;
}

.faq-container {
  max-width: 1000px;
  width: calc(100% - 40px);
  margin: 20px auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.faq-item {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 10px;
  width: 100%;
}

.faq-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.faq-question {
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.faq-question > div {
  flex: 1;
  padding-right: 20px;
  font-size: 16px;
}

.faq-answer {
  padding: 0 20px 20px;
  line-height: 1.6;
  color: #555;
  font-size: 15px;
}

.faq-item.active {
  background-color: #f5f5f5;
}

.loading-state, .error-state {
  text-align: center;
  padding: 30px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
}

.retry-button {
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #e94057;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.retry-button:hover {
  background-color: #d13048;
}

.category-section {
  margin-bottom: 25px;
  width: 100%;
}

.category-title {
  padding: 15px 20px;
  background-color: #20232a;
  color: white;
  font-size: 1.3rem;
  font-weight: 400;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  margin-bottom: 0;
}

.search-results, .no-results {
  text-align: center;
  padding: 15px;
  margin-bottom: 15px;
  font-weight: 400;
  color: #333;
  width: 100%;
}

.no-results {
  color: #e94057;
}

.faq-category-tag {
  background-color: #e94057;
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  display: inline-block;
  margin: 15px 0 -10px 20px;
}

@media (max-width: 768px) {
  .faq-container {
    width: calc(100% - 30px);
    margin: 15px auto;
  }
}

@media (max-width: 480px) {
  .faq-container {
    width: calc(100% - 20px);
    margin: 10px auto;
  }
}

/* Skeleton Loader Styles */
.skeleton-category-section {
  margin-bottom: 25px;
  width: 100%;
}

.skeleton-category-title {
  padding: 15px 20px;
  background-color: #20232a;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  margin-bottom: 0;
}

.skeleton-category-text {
  width: 40%;
  height: 20px;
}

.skeleton-faq-item {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 10px;
  width: 100%;
}

.skeleton-faq-question {
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.skeleton-question-text {
  height: 18px;
}

.skeleton-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-box {
  background: linear-gradient(
    90deg,
    #e0e0e0 0%,
    #f0f0f0 20%,
    #f0f0f0 40%,
    #e0e0e0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style> 